"use client";

import { Link } from "react-router-dom";

import { fleetDashboardCardClasses } from "@/components/devices/fleet-dashboard-modern";
import { Card } from "@/components/ui-system";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { daysUntil, warrantyBucket, type WarrantyBucket } from "@/lib/devices/fleet-dashboard";
import { cn } from "@/lib/utils/cn";

export type WarrantyRow = {
  id: string;
  deviceUid: string;
  deviceName: string;
  customerName: string | null;
  dateOfIssue: Date | null;
  warrantyExpiry: Date | null;
};

const headClass = "text-sm font-semibold normal-case tracking-tight text-foreground";

type FleetWarrantyPanelProps = {
  rows: WarrantyRow[];
};

export function FleetWarrantyPanel({ rows }: FleetWarrantyPanelProps) {
  const now = Date.now();
  const sorted = [...rows].sort((a, b) => {
    const ba = warrantyBucket(a.warrantyExpiry, now);
    const bb = warrantyBucket(b.warrantyExpiry, now);
    const rank = (w: WarrantyBucket) =>
      w === "expired" ? 0 : w === "d30" ? 1 : w === "d90" ? 2 : w === "unknown" ? 4 : 3;
    const dr = rank(ba) - rank(bb);
    if (dr !== 0) {
      return dr;
    }
    const da = a.warrantyExpiry ? daysUntil(a.warrantyExpiry, now) : 9999;
    const db = b.warrantyExpiry ? daysUntil(b.warrantyExpiry, now) : 9999;
    return da - db;
  });

  return (
    <Card
      className={cn(
        fleetDashboardCardClasses,
        "flex flex-col overflow-hidden border-0 p-0 shadow-none ring-1 ring-border/70",
      )}
    >
      <div className="border-b border-border/60 px-ds-5 py-ds-4">
        <h2 className="font-sans text-sm font-semibold text-foreground">Warranty summary</h2>
        <p className="mt-0.5 text-caption text-muted">
          Set <span className="text-foreground/80">warranty_end</span> (ISO date), customer, and issue / install
          dates in device metadata to populate this table.
        </p>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {sorted.length === 0 ? (
          <p className="px-ds-4 py-ds-6 text-center text-caption text-muted">No devices in fleet match.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={headClass}>Device name</TableHead>
                <TableHead className={headClass}>Customer name</TableHead>
                <TableHead className={headClass}>Date of issue</TableHead>
                <TableHead className={headClass}>Date of warranty expiry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Link to={`/devices/${r.id}`} className="font-medium text-link hover:underline">
                      {r.deviceName}
                    </Link>
                    <div className="text-micro text-muted">{r.deviceUid}</div>
                  </TableCell>
                  <TableCell className="text-caption text-foreground">{r.customerName ?? "-"}</TableCell>
                  <TableCell className="tabular-nums text-caption text-muted">
                    {r.dateOfIssue ? r.dateOfIssue.toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell className="tabular-nums text-caption text-muted">
                    {r.warrantyExpiry ? r.warrantyExpiry.toLocaleDateString() : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Card>
  );
}
